import React, { useCallback, useState } from "react";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import PathwayTextInputGroup from "../../../_UI/Components/Inputs/PathwayTextInputGroup";
import PathwaySelectInputGroup from "../../../_UI/Components/Inputs/PathwaySelectInputGroup";
import { LoadingSpinnerButton } from "../../../_UI/Components/PathwayButtons";
import { ErrorMessage } from "../../../_UI/Components/Alerts/PathwayAlertMessages";
import { searchClientsFromProd, autoUpdateClientFromProd } from "../services/clients.actions";
import { clientReducerActions } from "../../../_services/store";

const ClientAutoUpdateFromProdModal = ({ showModal, closeModal }) => {
    const dispatch = useDispatch();
    const clientReducer = useSelector((state) => state.client);
    const clients = useSelector((state) => state.client.clients);
    const productionClients = useSelector((state) => state.client.productionClients);

    const [token, setToken] = useState("");
    const [productionClientUUID, setProductionClientUUID] = useState("");
    const [targetClientUUID, setTargetClientUUID] = useState("");

    const handleSearch = useCallback(() => {
        setProductionClientUUID("");
        dispatch(searchClientsFromProd({ token }));
    }, [dispatch, token]);

    const handleSubmit = useCallback(() => {
        dispatch(
            autoUpdateClientFromProd({
                token,
                productionClientUUID,
                targetClientUUID,
            })
        );
    }, [dispatch, token, productionClientUUID, targetClientUUID]);

    const handleClose = useCallback(() => {
        dispatch(clientReducerActions.resetSearchClientsFromProd());
        dispatch(clientReducerActions.resetAutoUpdateClientFromProdMessages());
        setToken("");
        setProductionClientUUID("");
        setTargetClientUUID("");
        closeModal();
    }, [dispatch, closeModal]);

    const hasSearchResults = productionClients && productionClients.length > 0;

    return (
        <Modal isOpen={showModal} size="lg">
            <ModalHeader toggle={handleClose}>Client Update aus Produktion</ModalHeader>
            <ModalBody>
                <FormWrapper>
                    <SearchRow>
                        <PathwayTextInputGroup
                            type="password"
                            label="Token aus Produktion"
                            name="token"
                            value={token}
                            handleChange={(e) => {
                                setToken(e.target.value);
                                setProductionClientUUID("");
                                dispatch(clientReducerActions.resetSearchClientsFromProd());
                            }}
                        />
                        <LoadingSpinnerButton
                            color="primary"
                            loading={!!clientReducer.searchClientsFromProdLoading}
                            disabled={!token}
                            onClick={handleSearch}
                        >
                            Suchen
                        </LoadingSpinnerButton>
                    </SearchRow>
                    <ErrorMessage useTopMargin={false} useBottomMargin={false}>
                        {clientReducer.searchClientsFromProdErrorMessage}
                    </ErrorMessage>
                    {hasSearchResults && (
                        <>
                            <PathwaySelectInputGroup
                                label="Client aus Produktion"
                                name="productionClientUUID"
                                value={productionClientUUID}
                                handleChange={(e) => setProductionClientUUID(e.target.value)}
                                showNullOption
                            >
                                {productionClients.map((client) => (
                                    <option key={client.externalUUID} value={client.externalUUID}>
                                        {client.name} ({client.externalUUID})
                                    </option>
                                ))}
                            </PathwaySelectInputGroup>
                            <PathwaySelectInputGroup
                                label="Lokaler Client (Ziel)"
                                name="targetClientUUID"
                                value={targetClientUUID}
                                handleChange={(e) => setTargetClientUUID(e.target.value)}
                                showNullOption
                            >
                                {clients.map((client) => (
                                    <option key={client.externalUUID} value={client.externalUUID}>
                                        {client.name} ({client.externalUUID})
                                    </option>
                                ))}
                            </PathwaySelectInputGroup>
                            <LoadingSpinnerButton
                                color="warning"
                                loading={!!clientReducer.autoUpdateClientFromProdLoading}
                                disabled={!token || !productionClientUUID || !targetClientUUID}
                                onClick={handleSubmit}
                            >
                                Update
                            </LoadingSpinnerButton>
                        </>
                    )}
                </FormWrapper>
                {clientReducer.autoUpdateClientFromProdSuccessMessage && (
                    <SuccessMessage>{clientReducer.autoUpdateClientFromProdSuccessMessage}</SuccessMessage>
                )}
                <ErrorMessage useTopMargin={true} useBottomMargin={false}>
                    {clientReducer.autoUpdateClientFromProdErrorMessage}
                </ErrorMessage>
            </ModalBody>
        </Modal>
    );
};

export default ClientAutoUpdateFromProdModal;

const FormWrapper = styled.div`
    display: grid;
    row-gap: var(--gap-l);
`;

const SearchRow = styled.div`
    display: grid;
    grid-template-columns: 1fr max-content;
    column-gap: var(--gap-l);
    align-items: end;
`;

const SuccessMessage = styled.p`
    color: green;
    margin-top: var(--gap-l);
    margin-bottom: 0;
`;
